import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map, of } from 'rxjs';

import { environment } from '@www-src/environments/environment';

export interface SignedInUser {
  name: string;
  initials: string;
}

interface SelfResponse {
  data?: { given_name?: string; family_name?: string; email?: string };
}

/**
 * The person signed in to CaseActive, if anyone: the account API reads its own login cookie, which the
 * browser sends from the website too. Asked only when a page needs it (the help pages, where readers come
 * from their portal; check()), once, in the browser only (a prerendered page has nobody); not signed in
 * (401) or not reachable leaves it empty, and the menu shows Log In.
 */
@Injectable({
  providedIn: 'root'
})
export class SignedInUserService {
  public readonly user = signal<SignedInUser | null>(null);

  private http = inject(HttpClient);
  private browser = isPlatformBrowser(inject(PLATFORM_ID));
  private asked = false;

  public check(): void {
    if (!this.browser || this.asked) {
      return;
    }

    this.asked = true;
    this.http.get<SelfResponse>(`${environment.ACCOUNT_API_URL}/v1/self`, { withCredentials: true }).pipe(
      map(response => signedInUser(response?.data)),
      catchError(() => of(null)),
    ).subscribe(user => this.user.set(user));
  }
}

/** "Alexandra Reyes" → AR; one name → its first letter; no name → the email's first letter. */
export function signedInUser(self: SelfResponse['data'] | undefined): SignedInUser | null {
  const given = (self?.given_name ?? '').trim();
  const family = (self?.family_name ?? '').trim();
  const email = (self?.email ?? '').trim();
  const name = [given, family].filter(Boolean).join(' ') || email;

  if (!name) {
    return null;
  }

  const initials = given || family ? `${given.charAt(0)}${family.charAt(0)}` : email.charAt(0);

  return { name, initials: initials.toUpperCase() };
}

/** The help pages, in English or under a language (/help, /help/client, /de/help/…): readers come there from their portal. */
export function isHelpPage(url: string): boolean {
  return /^\/([a-z]{2}\/)?help(\/|\?|#|$)/.test(url);
}
